import { useRef, useMemo } from 'react';
import styled from 'styled-components';
import { s } from '../../PeriodStatsPage.styles';
import { downloadMultiSheetExcel } from '../../../../../utils/downloadAsExcel';
import type { NPLTrendRow } from './NPLTrendAnalysisGraph';
import StackedCountChart from '../../NPL/StackedCountChart';
import SimpleLineChart from './SimpleLineChart';
import SaveAsImageButton from '../../../../components/button/SaveAsImageButton';

type AnnualNationalNPLTrendProps = {
  region: string;
  usage: string;
  usageDetail?: string;
  court?: string;
  fiMain?: string;
  fiSub?: string;
  type: 'week' | 'month' | 'year';
  data: NPLTrendRow[];
  loading?: boolean;
  error?: string | null;
};

export default function AnnualNationalNPLTrend({
  region,
  usage,
  usageDetail,
  court,
  fiMain,
  fiSub,
  type,
  data = [],
  loading = false,
  error = null,
}: AnnualNationalNPLTrendProps) {
  const targetRef = useRef<HTMLDivElement>(null);

  const periodLabel = useMemo(() => {
    switch (type) {
      case 'week':
        return '주별';
      case 'month':
        return '월별';
      case 'year':
      default:
        return '연별';
    }
  }, [type]);

  const isWeek = type === 'week';

  const periodFileName = useMemo(() => {
    switch (type) {
      case 'week':
        return '주간_전국_NPL_지표_현황';
      case 'month':
        return '연간_전국_NPL_지표_현황';
      case 'year':
      default:
        return '연간_전국_NPL_지표_현황';
    }
  }, [type]);


  // 숫자 유틸
  const toNum = (v: any) =>
    typeof v === 'string' ? Number(v.replaceAll(',', '')) : Number(v) || 0;

  // 억원 단위로 변환한 렌더링용 데이터
  const amountData = useMemo(
    () =>
      (data ?? []).map((d) => ({
        ...d,
        mortgage_uk: toNum(d.mortgageAmount) / 1e8,
        claim_uk: toNum(d.claimPrice) / 1e8,
      })),
    [data],
  );

  // MSD 규칙 축 최대치
  const axisMaxByMSDRule = (n: number) => {
    if (!isFinite(n) || n <= 0) return 1;
    const intPart = Math.floor(n);
    if (intPart === 0) return 1;
    const digits = Math.floor(Math.log10(intPart)) + 1;
    const msd = Math.floor(intPart / Math.pow(10, digits - 1));
    return (msd + 1) * Math.pow(10, digits - 1);
  };

  // 엑셀(시트 4개: 금액/낙찰률군/담보회수군/손실부족군)
  const EXCEL_SHEETS = useMemo(() => {
    const sheetAmount = amountData.map((d) => ({
      기간: d.label,
      근저당액_억원: Number(d.mortgage_uk.toFixed(1)),
      청구금액_억원: Number(d.claim_uk.toFixed(1)),
    }));
    const sheetWin = data.map((d) => ({
      기간: d.label,
      낙찰률_퍼센트: Number(d.winRate.toFixed(1)),
      낙찰가율_퍼센트: Number(d.winPriceRate.toFixed(1)),
    }));
    const sheetCollRec = data.map((d) => ({
      기간: d.label,
      담보가치율_퍼센트: Number(d.collateralValueRate.toFixed(1)),
      회수율_퍼센트: Number(d.recoveryRate.toFixed(1)),
    }));
    const sheetLossShort = data.map((d) => ({
      기간: d.label,
      손실률_퍼센트: Number(d.lossRate.toFixed(1)),
      회수부족율_퍼센트: Number(d.shortfallRate.toFixed(1)),
    }));
    return { sheetAmount, sheetWin, sheetCollRec, sheetLossShort };
  }, [data, amountData]);

  const handleDownloadExcel = () => {
    downloadMultiSheetExcel(
      {
        [`${periodLabel} 금액(근저당액·청구금액)`]: EXCEL_SHEETS.sheetAmount,
        [`${periodLabel} 낙찰률·낙찰가율`]: EXCEL_SHEETS.sheetWin,
        [`${periodLabel} 담보가치율·회수율`]: EXCEL_SHEETS.sheetCollRec,
        [`${periodLabel} 손실률·회수부족율`]: EXCEL_SHEETS.sheetLossShort,
      },
      periodFileName,
    );
  };

  return (
    <s.Wrap ref={targetRef}>
      <s.Header>
        <s.HeaderRow>
          <s.Img $bg="#feefef">
            <img src="/assets/images/icon/red_calendar.svg" alt="" />
          </s.Img>
          <s.ButtonDiv>
            <SaveAsImageButton
              targetRef={targetRef}
              fileName={`${periodFileName}.png`}
            />
            <s.ExcelButton onClick={handleDownloadExcel} aria-label="엑셀 저장">
              <img src="/assets/images/icon/download-black.svg" alt="" />
            </s.ExcelButton>
          </s.ButtonDiv>
        </s.HeaderRow>
        <s.TitleBox>
        {isWeek ? (
          <h3>주간 전국 NPL 지표 현황</h3>
          ) : (
           <h3>연간 전국 NPL 지표 현황</h3>
          )}
          <p>근저당액·청구금액 및 주요 비율 지표 추이</p>
          <s.SelectedOptionBox>
            <s.SelectedOption>
              {court && court !== 'all' ? court : `${region} 지역`}
            </s.SelectedOption>
            <s.SelectedOption>
              {usage === '전체'
                ? `${usage} 용도`
                : `${usage} ${usageDetail ? ` ${usageDetail} 용도` : ''}`}
            </s.SelectedOption>
            <s.SelectedOption>
              {fiMain === '전체'
                ? `${fiMain} 금융기관`
                : `${fiSub ? ` ${fiSub}` : ''}`}
            </s.SelectedOption>
          </s.SelectedOptionBox>
        </s.TitleBox>
      </s.Header>

      {loading ? (
        <LoaderWrap>
          <LoaderImg src="/assets/images/icon/loader.gif" alt="NPL 지표" />
        </LoaderWrap>
      ) : (
        <Container>
          {/* 1) 근저당액·청구금액 */}
          <Section>
            <GraphTitle>근저당액·청구금액</GraphTitle>
            <GraphContainer>
              <StackedCountChart
                data={data}
                height={320}
                tickWidth={80}
                leftAxisWidth={50}
              />
              <LabelRow>
                <GraphLabel>
                  <LabelMarker $color="#E8E9ED" />
                  근저당액
                </GraphLabel>
                <GraphLabel>
                  <LabelMarker $color="#424854" />
                  청구금액
                </GraphLabel>
              </LabelRow>
            </GraphContainer>
          </Section>

          {/* 2) 낙찰률·낙찰가율 */}
          <Section>
            <GraphTitle>낙찰률·낙찰가율</GraphTitle>
            <GraphContainer>
              <SimpleLineChart
                data={data}
                dataKey1="winRate"
                dataKey2="winPriceRate"
                color1="#0F9667"
                color2="#3F6EF1"
                label1="낙찰률"
                label2="낙찰가율"
                yLabel="비율"
              />
              <LabelRow>
                <DotProgress>
                  <ProgressBar $color="#0F9667" />
                  <Dot $color="#0F9667" />
                  <ProgressBar $color="#0F9667" />
                  <LabelTag>낙찰률</LabelTag>
                </DotProgress>
                <DotProgress $width="92px">
                  <ProgressBar $color="#3F6EF1" />
                  <Dot $color="#3F6EF1" />
                  <ProgressBar $color="#3F6EF1" />
                  <LabelTag>낙찰가율</LabelTag>
                </DotProgress>
              </LabelRow>
            </GraphContainer>
          </Section>

          {/* 3) 담보가치율·회수율 */}
          <Section>
            <GraphTitle>{periodLabel} 담보가치율·회수율</GraphTitle>
            <GraphContainer>
              <SimpleLineChart
                data={data}
                dataKey1="collateralValueRate"
                dataKey2="recoveryRate"
                color1="#F15171"
                color2="#8E51F7"
                label1="담보가치율"
                label2="회수율"
                yLabel="비율"
              />

              <LabelRow>
                <DotProgress $width="103px">
                  <ProgressBar $color="#F15171" />
                  <Dot $color="#F15171" />
                  <ProgressBar $color="#F15171" />
                  <LabelTag>담보가치율</LabelTag>
                </DotProgress>
                <DotProgress>
                  <ProgressBar $color="#8E51F7" />
                  <Dot $color="#8E51F7" />
                  <ProgressBar $color="#8E51F7" />
                  <LabelTag>회수율</LabelTag>
                </DotProgress>
              </LabelRow>
            </GraphContainer>
          </Section>

          {/* 4) 손실률·회수부족율 */}
          <Section>
            <GraphTitle>{periodLabel} 손실률·회수부족율</GraphTitle>
            <GraphContainer>
              <SimpleLineChart
                data={data}
                dataKey1="lossRate"
                dataKey2="shortfallRate"
                color1="#CE5F16"
                color2="#787D87"
                label1="손실률"
                label2="회수부족율"
                yLabel="비율"
              />
              <LabelRow>
                <DotProgress>
                  <ProgressBar $color="#CE5F16" />
                  <Dot $color="#CE5F16" />
                  <ProgressBar $color="#CE5F16" />
                  <LabelTag>손실률</LabelTag>
                </DotProgress>
                <DotProgress $width="103px">
                  <ProgressBar $color="#787D87" />
                  <Dot $color="#787D87" />
                  <ProgressBar $color="#787D87" />
                  <LabelTag>회수부족율</LabelTag>
                </DotProgress>
              </LabelRow>
            </GraphContainer>
          </Section>
        </Container>
      )}
    </s.Wrap>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;
const RowGraph = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
const GraphContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const GraphTitle = styled.div`
  color: #0c0e12;
  font-size: 18px;
  font-weight: 500;
  line-height: 28px;
  letter-spacing: -0.36px;
`;

const GraphLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #424854;
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
`;

const DotProgress = styled.div<{ $width?: string }>`
  align-self: center;
  display: flex;
  flex-direction: row;
  gap: 2px;
  width: ${({ $width }) => $width ?? '80px'};
  align-items: center;
  margin: 8px;
`;

const ProgressBar = styled.div<{ $color?: string }>`
  height: 2px;
  flex: 1;
  background-color: ${({ $color }) => $color ?? '#0f9667'};
`;

const LabelMarker = styled.div<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  margin: 4px;
  background-color: ${({ $color }) => $color};
`;

const Dot = styled.div<{ $color?: string }>`
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background-color: ${({ $color }) => $color ?? '#0f9667'};
`;

const LabelTag = styled.div`
  font-size: 13px;
  color: #424854;
  margin-left: 4px;
  font-weight: 500;
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 24px;
  height: 24px;
`;
